/**
 * Therapie-Katalog schreiben: deckt sich mit medications_write /
 * treatments_write / diagnosis_therapy_ratings_write / care_task_rules_write
 * und resolve_catalog_review_item (medicus oder org-admin).
 * caregiver ist bewusst nicht enthalten. Die DB bleibt Autorität.
 */
export function canMaintainTherapyCatalog(roles: readonly string[]): boolean {
  return roles.includes("medicus") || roles.includes("admin");
}

export const CATALOG_WRITE_DENIED_MESSAGE =
  "Keine Berechtigung: Medikamente und den Therapie-Katalog dürfen nur Tiermedizin oder Administration anlegen und ändern.";

/** Sichtbarer Sperrhinweis je Bereich. Deny-Fehler bleiben bei CATALOG_WRITE_DENIED_MESSAGE. */
export const catalogAreaDeniedMessage = {
  medication:
    "Medikamente anlegen und ändern dürfen nur Tiermedizin oder Administration.",
  treatment:
    "Behandlungen anlegen und ändern dürfen nur Tiermedizin oder Administration.",
  rating:
    "Bewertungen anlegen und ändern dürfen nur Tiermedizin oder Administration.",
  standards: "Standards ändern dürfen nur Tiermedizin oder Administration.",
  review:
    "Review-Einträge sehen und bearbeiten dürfen nur Tiermedizin oder Administration.",
} as const;

const RATING_MED_DUPLICATE_MESSAGE = "Bewertung für dieses Medikament existiert bereits";
const RATING_TRT_DUPLICATE_MESSAGE = "Bewertung für diese Behandlung existiert bereits";
const REVIEW_ALREADY_RESOLVED_MESSAGE = "Dieser Review-Eintrag ist bereits erledigt.";
const MEDICATION_REQUIRED_MESSAGE = "Bitte ein Medikament auswählen.";
const TREATMENT_REQUIRED_MESSAGE = "Bitte eine Behandlung auswählen";

function errorParts(error: unknown): { code: string; text: string } {
  if (!error || typeof error !== "object") {
    return { code: "", text: error == null ? "" : String(error) };
  }
  const rec = error as { code?: unknown; message?: unknown; details?: unknown; hint?: unknown };
  const code = rec.code == null ? "" : String(rec.code);
  const text = [rec.message, rec.details, rec.hint]
    .filter((part) => part != null && String(part).trim() !== "")
    .map((part) => String(part))
    .join("\n");
  return { code, text };
}

/** RLS-Deny (42501) und der RPC-Check „medicus or admin required“. */
export function isCatalogWriteDenied(error: unknown): boolean {
  const { code, text } = errorParts(error);
  if (code === "42501") return true;
  return /row-level security|permission denied|medicus or admin required/i.test(text);
}

function constraintBlob(error: unknown, text: string): string {
  if (!error || typeof error !== "object" || !("constraint" in error)) return text;
  const name = (error as { constraint?: unknown }).constraint;
  if (name == null || String(name).trim() === "") return text;
  return `${text}\n${String(name)}`;
}

function warnRaw(raw: string) {
  const text = raw.trim();
  if (text) console.warn("Therapie-Katalog:", text);
}

export function catalogWriteErrorMessage(error: unknown, fallback: string): string {
  const { code, text } = errorParts(error);
  if (isCatalogWriteDenied(error)) return CATALOG_WRITE_DENIED_MESSAGE;
  if (code === "23505") {
    const blob = constraintBlob(error, text);
    if (blob.includes("diagnosis_therapy_ratings_med_uq")) return RATING_MED_DUPLICATE_MESSAGE;
    if (blob.includes("diagnosis_therapy_ratings_trt_uq")) return RATING_TRT_DUPLICATE_MESSAGE;
    warnRaw(blob);
    return fallback;
  }
  if (/review item already resolved/i.test(text)) return REVIEW_ALREADY_RESOLVED_MESSAGE;
  if (/medication_id required for accept/i.test(text)) return MEDICATION_REQUIRED_MESSAGE;
  if (/treatment_id required for accept/i.test(text)) return TREATMENT_REQUIRED_MESSAGE;
  warnRaw(text);
  return fallback;
}
